import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { Btn, Spinner } from '../../components/ui';
import { adminAPI } from '../../api';
import { canAdminister } from '../../lib/permissions';

import TabBar     from '../../components/TabBar';
import UsersTab   from './UsersTab';
import SchoolsTab from './SchoolsTab';
import PatchesTab from './PatchesTab';
import RolesTab   from './RolesTab';

// Data lives on its own screens in the bottom navigation; this page is for
// managing the things the data is about.
const TABS = [
  { k: 'users',    l: 'Users' },
  { k: 'schools',  l: 'Schools' },
  { k: 'patches',  l: 'Patches' },
  { k: 'roles',    l: 'Roles' },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tab, setTab]         = useState('users');
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const allowed = canAdminister(user);

  // Administration is a permission, not a page anyone can reach by URL.
  useEffect(() => {
    if (user && !allowed) {
      toast.error('You do not have access to administration.');
      navigate('/', { replace: true });
    }
  }, [user, allowed, navigate]);

  const reload = useCallback(() => {
    setLoading(true);
    adminAPI.options()
      .then(({ data }) => { setOptions(data); setError(''); })
      .catch(err => {
        console.error(err);
        setError('Could not load administration data. Please try again.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (allowed) reload(); }, [allowed, reload]);

  if (!allowed) return null;

  return (
    <div className="screen">
      <div className="obsbar">
        <button className="obsbar-back" onClick={() => navigate('/')}>← Back</button>
        <div className="obsbar-title">Manage</div>
      </div>

      <div className="adm-tabs">
        {TABS.map(t => (
          <button key={t.k} className={`adm-tab${tab === t.k ? ' on' : ''}`}
                  onClick={() => setTab(t.k)}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="scroll">
        <div className="pad">
          {loading && (
            <div style={{ display:'flex', justifyContent:'center', padding:'48px 0' }}>
              <Spinner />
            </div>
          )}

          {!loading && error && (
            <>
              <div className="dash-empty">{error}</div>
              <Btn variant="outline" onClick={reload}>Try again</Btn>
            </>
          )}

          {!loading && !error && (
            <>
              {tab === 'users'    && <UsersTab options={options} onChanged={reload} />}
              {tab === 'schools'  && <SchoolsTab options={options} onChanged={reload} />}
              {tab === 'patches'  && <PatchesTab onChanged={reload} />}
              {tab === 'roles'    && <RolesTab />}
            </>
          )}
        </div>
      </div>

      <TabBar current="admin" />
    </div>
  );
}
